import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, useMutation } from "@tanstack/react-query";
import useExportData from '../../hooks/useExportData';
import { useSorting } from '../../hooks/useSorting';
import useDebounce from '../../hooks/useDebounce';
import useTableStyles from '../../hooks/useTableStyles';
import { crossTabCommunication } from '../../hooks/useCrossTabCommunication';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { useUserContext } from '../../context/user';
import { AdsResponse, AdsSortField, AdsListStatus, AdStatus, getAdStatusText, isActiveAdStatus, AdsType } from '../../types/api/ads';
import { formatDate, toUTCStartOfDay, toUTCEndOfDay } from '../../helpers/dates';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import Segment from '../navigation/Segment/Segment';
import { pageSize } from '../../services/settings';
import { adsTabsData } from '../navigation/Segment/Segment+Data';
import { Placeholder, createErrorPlaceholder } from '../basic';
import { useAlert } from '../../context/alert';
import AdsFilterFormModal from '../user/Ads/components/FilterModal/AdsFilterFormModal';
import TableActionsBanner from '../common/TableActionsBanner';
import InfoActionsBanner from '../common/InfoActionsBanner';
import { CreateAdsCommentModal } from './Components/CreateAdsCommentModal';
import { DisplayAdsCommentModal } from './Components/DisplayAdsCommentModal';
import { BackComponent } from '../../hooks/useSafeNavigation';
import { adsTableConfig } from './AdsList+Table';
import { locale } from '../../helpers/strings';
import { CheckAdsStatus } from './AdsList+CheckAdStatus';
import { TextInput, Stack, Checkbox, Label, IconButton, Button, Text, Pagination } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { SearchIcon, DownloadIcon, XCircleIcon, XCircleFillIcon, XIcon, BlockedIcon, TrashIcon, CodeOfConductIcon, CheckIcon, CommentIcon } from '@primer/octicons-react'
import { fetchAdsList, blockAd, activateAds, restoreAds, exportAds } from '../../services/ads';
import { useAdsQuery } from '../../hooks/useAdsQuery';
import { isAllowAction } from '../../services/permissions/utils';

const EMPTY_NUMBERS_ARRAY: number[] = [];
const EMPTY_STRINGS_ARRAY: string[] = [];
const EMPTY_SET = new Set<string>();

export default function AdsList() {
    type AdsAction = 'block' | 'confirm'

    const { profile } = useUserContext();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const rawAdsType = searchParams.get('type');
    const adsType = useMemo<AdsType>(
        () => (rawAdsType as AdsType) || "posted",
        [rawAdsType]
    );
    const rawCatalogId = searchParams.get('catalogId');
    const catalogId = useMemo<string | undefined>(
        () => rawCatalogId ?? undefined,
        [rawCatalogId]
    );
    const rawCategorygId = searchParams.get('categoryId');
    const categoryId = useMemo<string | undefined>(
        () => rawCategorygId ?? undefined,
        [rawCatalogId]
    );

    const [pageIndex, setPageIndex] = React.useState(1)

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const [selectedFilter, setSelectedFilter] = useState<AdsListStatus | undefined>(undefined);
    const [filterOpened, setFilterOpened] = useState<boolean>(false);

    // Color rows
    const [rowsToStyle, setRowsToStyle] = useState<number[]>(EMPTY_NUMBERS_ARRAY);
    const { removeStyles } = useTableStyles({
        rowsToStyle,
        bgColor: adsType === 'posted' || adsType === undefined ? '#DDF4FF99' : '#FFF8C599',
        borderColor: adsType === 'posted' || adsType === undefined ? '#54AEFF66' : '#D4A72C66'
    });

    const handleRemoveStyles = () => {
        removeStyles();
    };

    // Comments
    const [openCreateCommentModal, setOpenCreateCommentModal] = React.useState(false)
    const [openDisplayCommentModal, setOpenDisplayCommentModal] = React.useState(false)
    const [adsForDisplayComment, setAdsForDisplayComment] = useState<AdsResponse | undefined>(undefined);
    const [adsForCreateComment, setAdsForCreateComment] = useState<string[]>(EMPTY_STRINGS_ARRAY);

    // Filters Modal
    const [priceFilterOpened, setPriceFilterOpened] = useState<boolean>(false);
    const [dateFilterOpened, setDateFilterOpened] = useState<boolean>(false);

    // Filter request params
    const [fromPrice, setFromPrice] = useState<string>('');
    const [toPrice, setToPrice] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Filter draft params for modal
    const [fromPriceDraft, setFromPriceDraft] = useState<string>('');
    const [toPriceDraft, setToPriceDraft] = useState<string>('');
    const [fromDateDraft, setFromDateDraft] = useState<string>('');
    const [toDateDraft, setToDateDraft] = useState<string>('');

    // Prepare filter request params
    const fromPriceNumber = parseFloat(fromPrice);
    const toPriceNumber = parseFloat(toPrice);

    const createdFromIsoDate = fromDate ? toUTCStartOfDay(fromDate) : undefined;
    const createdToIsoDate = toDate ? toUTCEndOfDay(toDate) : undefined;

    const priceFrom = isNaN(fromPriceNumber) ? undefined : fromPriceNumber;
    const priceTo = isNaN(toPriceNumber) ? undefined : toPriceNumber;

    // Выделение в таблицк
    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);
    // Одно выделенное объявление, когда на нем (не через алерт) нажимается подтверждение или блок
    const currentActionAdRef = useRef<AdsResponse | null>(null);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);

    const queryClient = useQueryClient();

    const { isLoading: isLoadingExport, exportAction } = useExportData();
    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const {
        orderingParam,
        handleSort,
        getSortIcon,
        getSortText
    } = useSorting<AdsSortField>('created_at', 'asc');
    const orderingParams: AdsSortField = orderingParam as AdsSortField;

    // Reset pages when filters changed
    useEffect(() => {
        setPageIndex(1);
    }, [priceTo, priceFrom, fromDate, toDate]);

    useEffect(() => {
        setPageIndex(1);
        setSelectedRows(EMPTY_SET);
    }, [debouncedSearchQuery]);


    useEffect(() => {
        setPageIndex(1);
        setSelectedRows(EMPTY_SET);
    }, [debouncedSearchQuery]);

    useEffect(() => {
        const type = adsType as AdsType;
        let shouldRefetch = false;

        setSelectedRows(EMPTY_SET);

        switch (type) {
            case 'posted':
            case undefined:
                shouldRefetch = true;
                break;
            case 'requiresConfirmation':
            case 'archive':
                break;
        }
        if (shouldRefetch) {
            refetch();
        }
    }, [selectedFilter, pageIndex, adsType]);

    useEffect(() => {
        // Сбросить после переключения вкладок типов
        setPageIndex(1)
    }, [adsType]);

    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        refetch()
    }, [pageIndex]);


    useEffect(() => {
        const unsubscribe = crossTabCommunication.onMessage((message) => {
            if (message.type === 'LINK_VIEWED') {
                queryClient.invalidateQueries({ queryKey: ['ads', 'waiting'] });
                queryClient.setQueryData(['id', message.data.linkId], (old: any) => ({
                    ...old,
                    isViewed: true
                }));
            }
        });

        return unsubscribe;
    }, []);

    const sortDirection = orderingParams.startsWith('-') ? 'DESC' : 'ASC';
    const cleanedOrderingParams = orderingParams.replace(/^-/, '');

    const {
        isLoading,
        data,
        refetch,
        isFetched,
        isFetching,
        failureReason,
        isError
    } = useAdsQuery(
        adsType ?? 'posted',
        debouncedSearchQuery,
        pageIndex,
        pageSize,
        undefined, // isActive
        cleanedOrderingParams, // sort
        sortDirection, // ordering
        selectedFilter, // status
        createdFromIsoDate, // createdFrom
        createdToIsoDate, // createdTo
        priceFrom, // priceFrom
        priceTo, // priceTo,
        undefined, // userId
        undefined, // categoryId 
        catalogId // catalogId 
    );


    useCleanPaginationURL();
    usePaginationTextReplace()

    // Color rows
    useEffect(() => {
        setRowsToStyle(EMPTY_NUMBERS_ARRAY)
        removeStyles()

        if (data && adsType !== "archive") {
            const notViewedIndexes: number[] = data?.list
                .map((item, index) => ({ item, index }))
                .filter(({ item }) => !item.isViewed)
                .map(({ index }) => index + 1);
            setRowsToStyle(notViewedIndexes);
        }

    }, [data, adsType]);

    const handleAdsActionResult = (
        count: number,
        singularMessage: string,
        pluralMessage: string
    ) => {
        // Update UI
        dismissTableActionAlert()
        setSelectedRows(EMPTY_SET);
        refetch();
        // Alert
        if (count === 1) {
            setInfo(singularMessage);
        } else {
            setInfo(pluralMessage);
        }
    };

    // BLOCK AD
    function createConfirmBlockAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите заблокировать?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={blockSelectedAds}>
                    Да, заблокировать
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }


    // CONFIRM SINGLE AD
    function createConfirmOneAdAlertProps(id: string) {
        return {
            count: undefined,
            title: 'Точно хотите подтвердить объявление?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissConfirmOneAdAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="default" onClick={() => confirmAdsRequest({ ids: [id] })}>
                    Да, подтвердить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    function createBlockOneAdAlertProps(id: string) {
        return {
            count: undefined,
            title: 'Точно хотите заблокировать?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissConfirmOneAdAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={() => blockAdsRequest({ ids: [id] })}>
                    Да, заблокировать
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    const dismissConfirmOneAdAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()
    }, [actionsAlertId]);


    const showConfirmBlockAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        const props = createConfirmBlockAlertProps(selectedItemsCount)
        showUpdatedAlert(props)
    }, [actionsAlertId, currentActionAdRef]);

    const showSuccessBlockAlert = useCallback((adIds: string[]) => {
        const props = createSuccessBlockAlertProps()
        //showUpdatedAlert(props)
        setActionsAlertId(undefined)
        dismissTableActionAlert()

        const alertId = addCustomAlert(
            InfoActionsBanner,
            props,
            {
                autoClose: false,
            }
        );
        setAdsForCreateComment(adIds)

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId]);

    const showSuccessConfirmAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()

        let selectedItemsCount = selectedRowsRef.current.size
        handleAdsActionResult(selectedItemsCount, 'Объявление подтверждено', selectedItemsCount + ' объявления подтверждены')
        setSelectedRows(EMPTY_SET);
    }, [actionsAlertId]);

    function createSuccessBlockAlertProps() {
        return {
            title: 'Объявление заблокировано',
            buttons: [
                <Button key="clear-button" variant="default" onClick={openCreateCommentsForSelectedAdsModal}>
                    Оставить комментарий
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    function createConfirmAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите подтвердить?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="default" onClick={confirmSelectedAds}>
                    Да, подтвердить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    const showAllowAdAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        let currentAd = currentActionAdRef.current

        if (selectedItemsCount > 0) {
            let selectedItemsCount = selectedRowsRef.current.size
            const props = createConfirmAlertProps(selectedItemsCount)
            showUpdatedAlert(props)
        } else if (currentAd) {
            const props = createConfirmOneAdAlertProps(currentAd.id)
            const existedAlertId = actionsAlertIdRef.current
            if (existedAlertId) {
                updateAlert(existedAlertId, {
                    customProps: props
                });
            } else {
                const alertId = addCustomAlert(
                    TableActionsBanner,
                    props,
                    {
                        autoClose: false,
                    }
                );

                setActionsAlertId(alertId)
            }
        }
    }, [actionsAlertId]);

    const showDenyAdAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        let currentAd = currentActionAdRef.current

        if (selectedItemsCount > 0) {
            let selectedItemsCount = selectedRowsRef.current.size
            const props = createConfirmAlertProps(selectedItemsCount)
            showUpdatedAlert(props)
        } else if (currentAd) {
            const props = createBlockOneAdAlertProps(currentAd.id)
            const existedAlertId = actionsAlertIdRef.current
            if (existedAlertId) {
                updateAlert(existedAlertId, {
                    customProps: props
                });
            } else {
                const alertId = addCustomAlert(
                    TableActionsBanner,
                    props,
                    {
                        autoClose: false,
                    }
                );

                setActionsAlertId(alertId)
            }
        }
    }, [actionsAlertId]);

    // ADS ACTIONS
    const { mutate: blockAdsRequest } = useMutation({
        mutationFn: ({ ids }: {
            ids: string[],
        }) => blockAd(ids),
        onSuccess: (data, variables) => {
            refetch()
            showSuccessBlockAlert(variables.ids)
        }
    });

    const { mutate: confirmAdsRequest } = useMutation({
        mutationFn: ({ ids }: {
            ids: string[],
        }) => activateAds(ids),
        onSuccess: (data) => {
            refetch()
            showSuccessConfirmAlert()
        }
    });
    // ALX-60312: вернуть в работу объявление, отклонённое антифродом
    const { mutate: restoreAdsRequest } = useMutation({
        mutationFn: ({ ids }: {
            ids: string[],
        }) => restoreAds(ids),
        onSuccess: (data, variables) => {
            refetch()
            handleAdsActionResult(
                variables.ids.length,
                'Объявление возвращено в работу',
                variables.ids.length + ' объявления возвращены в работу'
            )
        }
    });

    const restoreSingleAd = (ads: AdsResponse) => {
        restoreAdsRequest({ ids: [ads.id] })
    }

    // ALX-60743: по ТЗ одна кнопка "Заблокировать" на всех вкладках, бэк сам принимает
    // ожидающие и подтверждённые. Выбор ручки по вкладке держал устаревший adsType в замыкании
    const blockSelectedAds = useCallback(() => {
        const ids = Array.from(selectedRowsRef.current);
        if (ids.length === 0) return;

        blockAdsRequest({ ids });
    }, [blockAdsRequest]);

    const confirmSelectedAds = useCallback(() => {
        const ids = Array.from(selectedRowsRef.current);

        console.log('blockSelectedAds called');
        console.log('selectedRowsRef.current:', selectedRowsRef.current);
        console.log('selectedRowsRef.current size:', selectedRowsRef.current?.size);
        console.log('selectedRows state:', selectedRows);
        console.log('selectedRows state size:', selectedRows.size);
        console.log('adsType:', adsType);
        console.log('ids array:', ids);
        if (ids.length === 0) {
            console.error('empty...');
            console.error('set', selectedRowsRef.current instanceof Set);
            console.error('selectedRows (state):', selectedRows);
            console.error('selectedRows size:', selectedRows.size);
            console.error('Ref & state', selectedRowsRef.current === selectedRows);
            return;
        }

        if (ids.length === 0) return;

        if (adsType === 'requiresConfirmation') {
            confirmAdsRequest({ ids });
        }
    }, [adsType]);

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
            setSelectedRows(EMPTY_SET);
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    // Плашка блокировки живёт без автозакрытия, чтобы успеть нажать «Оставить
    // комментарий». Она относится к только что заблокированному объявлению,
    // поэтому при смене вкладки и при уходе с экрана её надо гасить: иначе она
    // висела поверх архива и выглядела как ошибка при разблокировке (ALX-60503)
    useEffect(() => {
        return () => {
            const alertId = actionsAlertIdRef.current
            if (alertId) {
                removeAlert(alertId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const previousAdsTypeRef = useRef(adsType);
    useEffect(() => {
        if (previousAdsTypeRef.current === adsType) {
            return
        }
        previousAdsTypeRef.current = adsType
        dismissTableActionAlert()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adsType])

    const selectedRowsRef = useRef(selectedRows);
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows])

    const showTableActionsAlert = useCallback(() => {
        let selectedItems = selectedRowsRef.current
        let count = Array.from(selectedItems).length
        handleAddTableAlert(count)
    }, [actionsAlertId]);

    const handleAddTableAlert = useCallback((count: number) => {
        let props: any = undefined

        switch (adsType) {
            case 'posted':
            case undefined:
                props = createBlockPostedAdsAlertProps(count)
                break
            case 'requiresConfirmation':
                props = createRequiresConfirmAdsAlertProps(count)
                break
            case 'archive':
                break
        }

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, data, adsType]);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        let selectedItemsCount = selectedRowsRef.current.size
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        }
    }, [actionsAlertId]);

    // TABLE ACTIONS ALERT
    function createBlockPostedAdsAlertProps(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);
        return {
            count: count,
            title: title,
            buttons: [
                <Button key="clear-button" variant="default" leadingVisual={BlockedIcon} onClick={showConfirmBlockAlert}>
                    Заблокировать
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    function createRequiresConfirmAdsAlertProps(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);
        const allowConfirm = isAllowAction(profile, 'APPROVE_ADS')
        const allowBlock = isAllowAction(profile, 'BLOCK_ADS')

        return {
            count: count,
            title: title,
            buttons: [
                <Button key="clear-button" variant="default" leadingVisual={CheckIcon} disabled={!allowConfirm} onClick={showAllowAdAlert}>
                    Подтвердить
                </Button>,
                <Button key="clear-button" variant="default" leadingVisual={XIcon} disabled={!allowBlock} onClick={showConfirmBlockAlert}>
                    Заблокировать
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    // SELECT ROWS
    const handleSelectAll = useCallback((isSelected: boolean) => {
        if (data) {
            if (isSelected) {
                const activeAds = data.list.filter(el => isActiveAdStatus(el.status))
                setSelectedRows(new Set(activeAds.map(row => row.id)));
            } else {
                setSelectedRows(EMPTY_SET);
            }
        }
    }, [data]);

    const headerCheckboxState = useMemo(() => {
        if (data && data.list.length > 0) {
            const activeAds = data.list.filter(el => isActiveAdStatus(el.status))
            const totalRows = activeAds.length;
            const selectedFromCurrentData = activeAds.filter(row => selectedRows.has(row.id)).length;

            if (selectedFromCurrentData === 0) {
                return { checked: false, indeterminate: false };
            } else if (selectedFromCurrentData === totalRows && totalRows > 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }
        return { checked: false, indeterminate: false };
    }, [data, selectedRows]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        rowSelect(rowId, isSelected)
    }, [handleAddTableAlert]);

    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    useEffect(() => {
        let selectedItemsCount = selectedRows.size
        if (adsForCreateComment.length > 0) {
            // Не нужно удалять алерт, т.к обновляем текущий 
            // (алерт с предложением создать комментарий)
            return
        }

        if (selectedItemsCount > 0) {
            handleAddTableAlert(selectedItemsCount)
        } else {
            if (actionsAlertId) {
                removeAlert(actionsAlertId)
                setActionsAlertId(undefined)
            }
        }
    }, [selectedRows])

    // CHECK Ads STATUS
    const selectedIsActive = (isActive: boolean): boolean => {
        let selectedAds = selectedRowsRef.current
        const ids = Array.from(selectedAds);
        let checker = new CheckAdsStatus(data?.list || [])
        return checker.allActiveStatus(ids, isActive);
    };

    // EXPORT Ads
    const downloadAds = async () => {
        try {
            const result = await exportAction(exportAds, "ads", "xlsx");
            if (result.success) {
                setSuccess('Загрузка списка объявлений успешно началась');
            }
            // Ошибку можно не показывать т.к toast вызовется уже в серверном слое
        } catch (err) { }
    }

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: [searchQuery, pageIndex] })
    }, [pageIndex]);

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    // FILTER MODAL
    const handleDismiss = () => {
        setFromPrice('');
        setToPrice('');
        setFromDate('');
        setToDate('');

        setFromPriceDraft('');
        setToPriceDraft('');
        setFromDateDraft('');
        setToDateDraft('');

        setFilterOpened(false);
    }
    const handleApply = () => {
        setFromPrice(fromPriceDraft);
        setToPrice(toPriceDraft);
        setFromDate(fromDateDraft);
        setToDate(toDateDraft);

        setFilterOpened(false);
    }

    // ROUTER
    const openAdsPage = (ads: AdsResponse) => {
        navigate('/ads/ad?id=' + ads.id)
    }

    const emptyListDescription = `По вашему запросу не найдено
подходящих объявлений`;

    const confirmSingleAd = (ad: AdsResponse) => {
        currentActionAdRef.current = ad;
        showAllowAdAlert()
    }

    const blockSingleAd = (ad: AdsResponse) => {
        currentActionAdRef.current = ad;
        showDenyAdAlert()
    };

    const openCreateCommentsForSelectedAdsModal = () => {
        dismissTableActionAlert()
        setOpenCreateCommentModal(true)
    }

    const commentCreateModalClosed = () => {
        setOpenCreateCommentModal(false)
        setAdsForCreateComment(EMPTY_STRINGS_ARRAY)
    }

    const openDisplayCommentsAdModal = (ad: AdsResponse) => {
        setOpenDisplayCommentModal(true)
        setAdsForDisplayComment(ad)
    }

    const commentDisplayModalClosed = () => {
        setOpenDisplayCommentModal(false)
        setAdsForDisplayComment(undefined)
    }

    const commentCreateModalSended = () => {
        setOpenCreateCommentModal(false)
        refetch()
    }

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <Stack direction="horizontal">
                        {categoryId && <BackComponent fallbackPath={`/catalogs/?id=${categoryId}`} isOnlyFallback={true} />}
                        <Segment items={adsTabsData(profile, searchParams)} />
                    </Stack>

                    {adsForCreateComment.length > 0 && <CreateAdsCommentModal
                        isOpen={openCreateCommentModal}
                        adIds={adsForCreateComment}
                        onDidCommentSended={commentCreateModalSended}
                        onClose={commentCreateModalClosed}
                    />}

                    {(adsForDisplayComment && adsForDisplayComment.hasAdminComments) && <DisplayAdsCommentModal
                        isOpen={openDisplayCommentModal}
                        ads={adsForDisplayComment}
                        comment={"adsForDisplayComment.hasAdminComments"}
                        onClose={commentDisplayModalClosed}
                    />}
                    {(adsType === 'posted' || adsType === undefined) && (
                        <Stack direction="horizontal">
                            <TextInput
                                leadingVisual={SearchIcon}
                                trailingAction={
                                    searchQuery.length > 0 ? (
                                        <TextInput.Action
                                            onClick={() => {
                                                setSearchQuery('');
                                            }}
                                            icon={XCircleFillIcon}
                                            aria-label="Очистить"
                                        />
                                    ) : undefined
                                }
                                value={searchQuery}
                                placeholder='Поиск'
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                }}
                            />

                            <AdsFilterFormModal
                                filterOpened={filterOpened}
                                setFilterOpened={setFilterOpened}

                                priceFilterOpened={priceFilterOpened}
                                setPriceFilterOpened={setPriceFilterOpened}

                                dateFilterOpened={dateFilterOpened}
                                setDateFilterOpened={setDateFilterOpened}

                                selectedFilter={selectedFilter}
                                setSelectedFilter={setSelectedFilter}

                                fromPrice={fromPriceDraft}
                                setFromPrice={setFromPriceDraft}
                                toPrice={toPriceDraft}
                                setToPrice={setToPriceDraft}

                                fromDate={fromDateDraft}
                                setFromDate={setFromDateDraft}
                                toDate={toDateDraft}
                                setToDate={setToDateDraft}

                                onDismiss={handleDismiss}
                                onApply={handleApply}
                            />

                            <IconButton
                                icon={DownloadIcon}
                                aria-label="Выгрузить объявления"
                                loading={isLoadingExport}
                                onClick={downloadAds} />
                        </Stack>
                    )}
                </Stack>
            }
            content={
                <Stack>
                    {isError && !isLoading && !isFetching && !failureReason?.name?.includes('AbortError') && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="ads-loading"
                            rows={pageSize}
                            columns={adsTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Ничего не было найдено'
                            description={emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="ads-table"
                                data={data.list}
                                columns={[
                                    ...(adsType === 'posted' || adsType === 'requiresConfirmation' || adsType === undefined ? [{
                                        id: 'select',
                                        header: (() => <Checkbox
                                            checked={headerCheckboxState.checked}
                                            indeterminate={headerCheckboxState.indeterminate}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleSelectAll(e.target.checked)
                                            }
                                            aria-label="Выделить все строки"
                                        />),
                                        field: undefined,
                                        rowHeader: true,
                                        maxWidth: 40,
                                        renderCell: (ads: AdsResponse) => {
                                            const isInArchive = !isActiveAdStatus(ads.status)
                                            return !isInArchive && (
                                                <Checkbox
                                                    checked={selectedRows.has(ads.id)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleRowSelect(ads.id, e.target.checked)
                                                    }
                                                    aria-label={`Выделить ${ads.title}`}
                                                />
                                            )
                                        }
                                    }] : []),
                                    {
                                        id: 'id',
                                        header: 'ID',
                                        rowHeader: true,
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            return <button
                                                onClick={() => openAdsPage(ads)}>
                                                {`ID${ads.number ?? ads.id}`}
                                            </button>
                                        }
                                    },
                                    {
                                        id: 'name',
                                        rowHeader: true,
                                        header: (() =>
                                            "Наименование"
                                        ),
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            const mainImage = ads.media?.[0]?.preview || ads.media?.[0]?.base64Preview;

                                            return <button
                                                onClick={() => openAdsPage(ads)}>
                                                <Stack direction="horizontal" gap="condensed">
                                                    <div
                                                        className="bg-header border border-border rounded-md flex-shrink-0"
                                                        style={{
                                                            backgroundImage: `url("${mainImage && mainImage.trim() ? mainImage : '/icons/avatar.png'}")`,
                                                            backgroundSize: 'cover',
                                                            backgroundPosition: 'center',
                                                            backgroundRepeat: 'no-repeat',
                                                            width: "24px",
                                                            height: "24px"
                                                        }}
                                                    />
                                                    {ads.title}
                                                </Stack>
                                            </button>
                                        }
                                    },
                                    {
                                        id: 'username',
                                        header: (() =>
                                            <button onClick={() => handleSort("username")}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon("username")}
                                                    <Text className="ml-[4.5px]">{getSortText("username", 'Имя', 'А-Я', 'Я-А')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            return ads.user?.username
                                        }
                                    },
                                    {
                                        id: 'category',
                                        header: (() =>
                                            <button onClick={() => handleSort('category')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('category')}
                                                    <Text className="ml-[4.5px]">{getSortText('category', 'Раздел', 'А-Я', 'Я-А')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            return ads?.category?.title
                                        }
                                    },
                                    {
                                        id: 'catalog',
                                        header: (() =>
                                            <button onClick={() => handleSort('catalog')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('catalog')}
                                                    <Text className="ml-[4.5px]">{getSortText('catalog', 'Подраздел', 'А-Я', 'Я-А')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            return ads.catalog?.title
                                        }
                                    },
                                    {
                                        id: 'status',
                                        header: ('Статус'),
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            const isInArchive = !isActiveAdStatus(ads.status)
                                            // В архиве раньше у любого неактивного статуса стояло «Удалено»,
                                            // и отклонённое объявление было неотличимо от истёкшего и закрытого.
                                            // Из-за этого непонятно, почему кнопка возврата в работу есть
                                            // не у всех строк - она только у отклонённых (ALX-60288)
                                            const statusVariant = ads.status === AdStatus.REJECTED
                                                ? 'danger'
                                                : (isInArchive ? 'default' : 'success')
                                            return (<Label variant={statusVariant} size="small">{getAdStatusText(ads.status)}</Label>)
                                        }
                                    },
                                    {
                                        id: 'price',
                                        header: (() =>
                                            <button onClick={() => handleSort('price')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('price')}
                                                    <Text className="ml-[4.5px]">{getSortText('price', 'Цена')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            return ads.price
                                        }
                                    },
                                    {
                                        id: 'date',
                                        header: (() =>
                                            <button onClick={() => handleSort('created_at')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('created_at')}
                                                    <Text className="ml-[4.5px]">{getSortText('created_at', 'Дата размещения')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const ads = row as AdsResponse;
                                            return formatDate(ads.createdAt)
                                        }
                                    },
                                    ...(adsType === 'requiresConfirmation' ? [{
                                        id: 'actions',
                                        header: (''),
                                        renderCell: (row: AdsResponse) => {
                                            const confirm = (<IconButton
                                                onClick={() => confirmSingleAd(row)}
                                                icon={CheckIcon}
                                                style={{ color: '#1A7F37' }}
                                                variant="invisible"
                                                disabled={selectedRows.size !== 0}
                                                className={selectedRows.size !== 0 ? 'opacity-50' : ''}
                                                aria-label="Подтвердить" />)
                                            const block = (<IconButton
                                                onClick={() => blockSingleAd(row)}
                                                icon={XIcon}
                                                style={{ color: '#D1242F' }}
                                                variant="invisible"
                                                disabled={selectedRows.size !== 0}
                                                className={selectedRows.size !== 0 ? 'opacity-50' : ''}
                                                aria-label="Заблокировать" />)
                                            return (
                                                <Stack direction="horizontal" gap="condensed" >
                                                    {isAllowAction(profile, 'APPROVE_ADS') && confirm}
                                                    {isAllowAction(profile, 'BLOCK_ADS') && block}
                                                </Stack>
                                            )
                                        }
                                    }] : []),
                                    ...(adsType === 'archive' ? [{
                                        id: 'restore',
                                        header: (''),
                                        renderCell: (ads: AdsResponse) => {
                                            // Возврат доступен только отклонённым: истёкшие и закрытые
                                            // объявления бэкенд под этот переход не пускает
                                            if (ads.status !== AdStatus.REJECTED) {
                                                return null
                                            }
                                            return isAllowAction(profile, 'APPROVE_ADS') && (
                                                <IconButton
                                                    onClick={() => restoreSingleAd(ads)}
                                                    icon={CheckIcon}
                                                    style={{ color: '#1A7F37' }}
                                                    variant="invisible"
                                                    disabled={selectedRows.size !== 0}
                                                    className={selectedRows.size !== 0 ? 'opacity-50' : ''}
                                                    aria-label="Вернуть в работу" />
                                            )
                                        }
                                    }] : []),
                                    ...(adsType === 'archive' ? [{
                                        id: 'comment',
                                        header: (''),
                                        renderCell: (ads: AdsResponse) => {
                                            return ads.hasAdminComments && (
                                                <IconButton
                                                    onClick={() => openDisplayCommentsAdModal(ads)}
                                                    icon={CommentIcon}
                                                    variant="invisible"
                                                    aria-label="Комментарий" />
                                            )
                                        }
                                    }] : []),
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>
    )
}